import { type MeResponse } from '@diario/common';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '../../../components/ui/avatar';
import { clasesDeBotonCuadrado } from '../../../components/ui/botonCuadrado';
import { LanguageSwitcher } from '../../../components/ui/languageSwitcher';
import { MapaApp } from './mapaApp';
import { SelloRol } from './selloRol';

export interface CabeceraProps {
	/** Cierra la sesión activa; la invoca `MapaApp` desde su entrada «cerrar sesión». Se recibe
	 * siempre, aunque solo tenga efecto real con sesión — decidir cuándo se puede montar el panel ya
	 * es cosa de esta propia cabecera, no de quien la usa. */
	onCerrarSesion: () => void;
	/**
	 * La sesión activa (`GET /me`), o `undefined` sin sesión O mientras todavía no se ha resuelto
	 * — en los dos casos la cabecera pinta la vista de visitante (regla del brief T-3.1: "con
	 * sesión indeterminada, pinta la cabecera sin sesión"). Se recibe por props, nunca con
	 * `useSesion` propio, para que este componente sea presentacional y testable sin red — el
	 * hook lo llama `__root.tsx`.
	 */
	sesion?: MeResponse;
}

/**
 * Cabecera fija de la aplicación (ADR-0022, oleada 3 — sustituye a la cabecera propia de
 * `specs/diseno-2026-09-02/`): la topbar completa del cliente. De izquierda a derecha: la
 * hamburguesa que abre "el mapa de la app" (`MapaApp`, panel lateral sobre `CapaModal`), la marca
 * responsive (`∂a` por debajo de 520 px, `∂iario aventureros` a partir de ahí — cierra H-30 y H-31,
 * ver el comentario junto al `Link` de la marca más abajo) y, a la derecha, el conmutador de idioma
 * más, según haya sesión o no, `SelloRol` + `Avatar` (32 px) o el enlace a `/login` vestido de
 * botón cuadrado (la puerta, regla de marca "cuadrado = la puerta, redondeado = la casa",
 * `docs/design-system.md`).
 *
 * Sin sesión no hay hamburguesa ni panel: los accesos de `MapaApp` exigen sesión igual que antes
 * exigía `MenuUsuario` (`puedeVerAcceso`), así que no hay nada que el panel pudiera enseñar. En su
 * lugar (P-12, oleada «rescate navegación» 2026-09-17), la cabecera enseña a quien no tiene cuenta
 * los dos únicos accesos que sí existen sin sesión — `/saber-mas` y `/roles` — porque sin ellos
 * ambas pantallas quedaban terminadas pero inalcanzables: era imposible llegar a ellas navegando.
 * Con sesión esta franja no aparece: el mapa de la app ya cubre la navegación.
 *
 * Dos piezas de la topbar del cliente NO se adoptan, a propósito (ADR-0022): el conmutador
 * «carta/app» (sin «modo carta» en este producto, ninguna iteración ingerida define qué
 * significaría) y la barra de cifras bajo la topbar (sin endpoint de agregados en este repositorio,
 * y el propio cliente la marca como demo).
 */
export const Cabecera = ({ onCerrarSesion, sesion }: CabeceraProps): React.JSX.Element => {
	const { t } = useTranslation(['shell', 'auth']);
	const [mapaAbierto, setMapaAbierto] = useState(false);

	return (
		<header className='sticky top-0 z-30 border-border border-b bg-paper/85 backdrop-blur'>
			<div className='mx-auto flex max-w-3xl items-center gap-2 px-3 py-3 sm:gap-4 sm:px-6'>
				{sesion ? (
					<button
						aria-label={t('mapa.abrir')}
						className='inline-flex min-h-11 min-w-11 shrink-0 flex-col items-center justify-center gap-1 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink'
						onClick={() => setMapaAbierto(true)}
						type='button'
					>
						<span aria-hidden='true' className='h-0.5 w-5 rounded-pill bg-ink' />
						<span aria-hidden='true' className='h-0.5 w-5 rounded-pill bg-ink' />
						<span aria-hidden='true' className='h-0.5 w-5 rounded-pill bg-ink' />
					</button>
				) : null}
				{/*
				 * H-30: el enlace de marca mide ahora ≥ 44 px de alto de verdad (`min-h-11 inline-flex
				 * items-center`, no solo visual). H-31: por debajo de 520 px (`min-[520px]:`, un corte
				 * propio, no el `sm` de 640 px del resto de la cabecera) se pinta solo la forma corta
				 * (`shell:marcaCorta`, «∂a»), así que la fila deja de depender de que la webfont ya haya
				 * cargado para caber a 320 px: la forma larga («∂iario aventureros», más ancha con la
				 * fuente de reserva que con Roboto 900) nunca se pinta en las anchuras donde H-31
				 * medía el desborde. La forma corta (`shell:marcaCorta`) es decorativa (`aria-hidden`):
				 * el nombre accesible del enlace lo da siempre el texto completo (`shell:marca`), en el
				 * DOM aunque oculto por CSS por debajo de 520 px.
				 */}
				<Link
					className='inline-flex min-h-11 min-w-0 flex-1 items-center font-black font-display text-ink text-lg'
					to='/'
				>
					<span aria-hidden='true' className='min-[520px]:hidden'>
						{t('marcaCorta')}
					</span>
					<span className='hidden min-[520px]:inline'>{t('marca')}</span>
				</Link>
				{sesion ? null : (
					// P-12: los dos únicos accesos que existen sin sesión, en la propia barra — la maqueta
					// del cliente los pone ahí, y son las dos pantallas que más responden por WhatsApp
					// (`shell:nav.sinSesion.*`, ver JSDoc de arriba). Enlaces de texto, no `BotonCuadrado`:
					// la puerta cuadrada es solo para «Entrar» (regla de marca "cuadrado = la puerta").
					<nav className='flex shrink-0 items-center gap-1 sm:gap-3'>
						<Link
							className='inline-flex min-h-11 items-center whitespace-nowrap rounded-sm px-2 font-semibold text-ink text-sm hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink sm:text-base'
							to='/saber-mas'
						>
							{t('nav.sinSesion.saberMas')}
						</Link>
						<Link
							className='inline-flex min-h-11 items-center whitespace-nowrap rounded-sm px-2 font-semibold text-ink text-sm hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink sm:text-base'
							to='/roles'
						>
							{t('nav.sinSesion.roles')}
						</Link>
					</nav>
				)}
				<div className='flex min-w-0 shrink items-center gap-2 sm:gap-3'>
					<LanguageSwitcher />
					{sesion ? (
						<>
							<SelloRol sesion={sesion} />
							<Avatar name={sesion.fullName ?? sesion.email} ring={true} size='sm' />
						</>
					) : (
						<Link className={clasesDeBotonCuadrado({ size: 'sm' })} to='/login'>
							{t('auth:conmutador.entrar')}
						</Link>
					)}
				</div>
			</div>
			{sesion ? (
				<MapaApp
					onCerrar={() => setMapaAbierto(false)}
					onCerrarSesion={onCerrarSesion}
					open={mapaAbierto}
					sesion={sesion}
				/>
			) : null}
		</header>
	);
};
